import { applyLanguage, isSupportedLanguage, tr } from "@/i18n";
import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, clearAccessToken } from "@/lib/api";

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  picture?: string | null;
  role: string;
  providers: string[];
  preferred_language?: string | null;
  credits_balance: number;
  subscription_status: string;
  subscription_plan?: string | null;
}

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  setUser: (u: AuthUser | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = React.createContext<AuthState>({
  user: null,
  loading: true,
  setUser: () => {},
  refresh: async () => {},
  logout: async () => {},
});

export const useAuth = () => React.useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Single entry point: any authenticated user (login, register, OAuth, refresh) applies the server-side language.
  const setUser = React.useCallback((u: AuthUser | null) => {
    setUserState(u);
    if (isSupportedLanguage(u?.preferred_language)) applyLanguage(u!.preferred_language!);
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get<AuthUser>("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  React.useEffect(() => {
    // CRITICAL: returning from OAuth — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = React.useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* session already gone */
    }
    clearAccessToken();
    setUser(null);
  }, [setUser]);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        data-testid="auth-checking"
        className="flex min-h-screen items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> {tr("common.checkingAccess")}
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
